import { Header } from "../components/promill/Header";
import { Footer } from "../components/promill/Footer";
import { useParams, Link } from "react-router-dom";
import { Scale, Shield, RefreshCcw, Cookie, ArrowLeft } from "lucide-react";

const LEGAL = {
    privacy: {
        icon: Shield,
        title: "Privacy Policy",
        subtitle: "How we collect, use and protect your information.",
        sections: [
            { h: "1. Information We Collect", p: "We collect information you provide when you enquire, subscribe to our newsletter, apply for a franchise, contact us, or otherwise engage with ProMill. This may include your name, email, phone number, city and any message content." },
            { h: "2. How We Use It", p: "We use your data to respond to enquiries, share product and store launch updates, process franchise applications, and improve our service. We never sell your data to third parties." },
            { h: "3. Storage & Security", p: "Data is stored on secure servers with industry-standard encryption. Access is limited to authorised ProMill staff on a need-to-know basis." },
            { h: "4. Your Rights", p: "You may request access, correction or deletion of your personal data at any time by emailing hello@promill.in." },
            { h: "5. Cookies & Analytics", p: "We use cookies and standard analytics tools to understand site usage. You can manage cookie preferences via your browser." },
            { h: "6. Third-Party Services", p: "Our site uses trusted partners for hosting, forms and analytics. These providers process data on our behalf under strict confidentiality." },
            { h: "7. Contact", p: "Questions about privacy? Email hello@promill.in or write to ProMill Foods Pvt. Ltd., Bengaluru, India." },
        ],
    },
    terms: {
        icon: Scale,
        title: "Terms & Conditions",
        subtitle: "The ground rules for using ProMill's website and services.",
        sections: [
            { h: "1. Acceptance", p: "By using promill.in, you agree to these Terms. If you don't agree, please don't use the site." },
            { h: "2. Products & Availability", p: "Our menu, prices and store locations are shown for information only and may change without notice. We reserve the right to modify or discontinue any product at any time." },
            { h: "3. Intellectual Property", p: "All content — logos, images, text, brand marks — is owned by ProMill Foods Pvt. Ltd. or its licensors. Do not reproduce without written permission." },
            { h: "4. User Conduct", p: "You agree not to misuse the site, attempt unauthorised access, or upload malicious content." },
            { h: "5. Third-Party Links", p: "We may link to third-party sites for convenience. We're not responsible for their content or practices." },
            { h: "6. Limitation of Liability", p: "ProMill is not liable for any indirect, incidental or consequential damages arising from your use of this site or our products." },
            { h: "7. Governing Law", p: "These Terms are governed by Indian law. Disputes fall under the exclusive jurisdiction of courts in Bengaluru, Karnataka." },
            { h: "8. Updates", p: "We may update these Terms occasionally. Continued use of the site constitutes acceptance of the revised Terms." },
        ],
    },
    refund: {
        icon: RefreshCcw,
        title: "Refund & Cancellation Policy",
        subtitle: "Fair, transparent and simple.",
        sections: [
            { h: "1. Enquiries & Waitlist", p: "Enquiries and waitlist sign-ups are free and non-transactional — no refunds apply." },
            { h: "2. Franchise Applications", p: "Franchise applications are free to submit. Any refundable deposits are governed by the separate franchise agreement between you and ProMill." },
            { h: "3. Product Orders (Future)", p: "Once our commercial ordering launches, orders may be cancelled before dispatch for a full refund. Post-dispatch cancellations are subject to our fulfillment policy at the time." },
            { h: "4. Quality Guarantee", p: "If any product falls short of our quality standard, contact us within 24 hours with photo evidence and we will refund or replace it." },
            { h: "5. Refund Timing", p: "Approved refunds are processed to the original payment method within 7 business days." },
            { h: "6. Contact", p: "For refund queries: hello@promill.in or WhatsApp +91 95177 17917." },
        ],
    },
    cookies: {
        icon: Cookie,
        title: "Cookie Policy",
        subtitle: "The tiny files that make our site work.",
        sections: [
            { h: "1. What Are Cookies?", p: "Cookies are small text files stored on your device by your browser. They help websites function properly and understand user behaviour." },
            { h: "2. How We Use Cookies", p: "ProMill uses cookies to remember your preferences (e.g. cart items in localStorage), analyse site usage, and improve your experience." },
            { h: "3. Types of Cookies", p: "Essential (site functionality), Preference (remember choices), Analytics (understand traffic patterns). We do not use advertising cookies." },
            { h: "4. Managing Cookies", p: "You can disable cookies in your browser settings. Note that some site features may not work correctly without them." },
            { h: "5. Third-Party Cookies", p: "Some third-party embeds (e.g. maps, video) may set their own cookies. Refer to their policies for details." },
            { h: "6. Updates", p: "We may update this policy occasionally. Please revisit periodically." },
        ],
    },
};

const LegalPage = () => {
    const { slug } = useParams();
    const data = LEGAL[slug];

    if (!data) {
        return (
            <div className="bg-pm-cream min-h-screen">
                <Header />
                <section className="py-24 text-center">
                    <h1 className="text-3xl font-extrabold text-pm-black">Page not found</h1>
                    <Link to="/" className="mt-4 inline-block text-pm-green font-bold hover:underline">← Back home</Link>
                </section>
                <Footer />
            </div>
        );
    }

    const Icon = data.icon;

    return (
        <div data-testid={`legal-${slug}-page`} className="bg-pm-cream min-h-screen">
            <Header />
            <section className="pt-10 md:pt-16 pb-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-pm-black/60 hover:text-pm-green font-bold mb-6">
                        <ArrowLeft className="w-4 h-4" /> Home
                    </Link>
                    <div className="flex items-start gap-5">
                        <div className="h-14 w-14 rounded-2xl bg-pm-green text-pm-yellow flex items-center justify-center shrink-0">
                            <Icon className="w-7 h-7" />
                        </div>
                        <div>
                            <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-2">Legal</span>
                            <h1 data-testid="legal-headline" className="text-3xl md:text-5xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                                {data.title}
                            </h1>
                            <p className="mt-2 text-pm-black/65">{data.subtitle}</p>
                            <p className="mt-1 text-xs text-pm-black/45">Last updated: December 2025</p>
                        </div>
                    </div>
                </div>
            </section>
            <section className="pb-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-2xl border border-pm-black/8 p-6 md:p-10 space-y-6">
                        {data.sections.map((s, i) => (
                            <div key={i} data-testid={`legal-section-${i}`}>
                                <h2 className="text-lg md:text-xl font-extrabold text-pm-black">{s.h}</h2>
                                <p className="mt-2 text-pm-black/70 leading-relaxed">{s.p}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default LegalPage;
